import { formatDateTime, useLocale, useTranslation } from '@multaqa/i18n'
import { Badge } from '@multaqa/ui/components/ui/badge'
import { Button } from '@multaqa/ui/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@multaqa/ui/components/ui/tooltip'
import { CogIcon, LockKeyholeIcon } from 'lucide-react'
import { useState } from 'react'
import type { AuditLogResource } from './api'
import { auditChangeValue } from './audit-change-value'
import { recordHref } from './audit-utils'

export function AuditCard({ rows, onOpen }: { rows: AuditLogResource[]; onOpen: () => void }) {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const [expanded, setExpanded] = useState(false)
  const event = rows[0]
  if (!event) return null
  const explicit =
    rows.find(
      (row) => !['created', 'updated', 'deleted'].includes(row.action.split('.').at(-1) ?? ''),
    ) ?? event
  const kind = event.action_kind
  const changes = rows.flatMap((row) =>
    row.changes.map((change) => ({
      ...change,
      actionKind: row.action_kind,
      key: `${row.id}-${change.field}`,
    })),
  )
  const href = recordHref(event.record.type, event.record.id)
  const action =
    kind === 'created'
      ? t('companyAudit.timeline.created')
      : kind === 'deleted'
        ? t('companyAudit.timeline.deleted')
        : kind === 'changed'
          ? t('companyAudit.timeline.changed')
          : explicit.action_label
  const actorName = event.actor.name || t('companyAudit.timeline.system')
  const recordLabel = event.record.label || event.record.type_label

  return (
    <article
      className="border-b border-border-subtle py-4 last:border-b-0"
      data-testid="audit-card"
    >
      <div className="flex w-full min-w-0 items-start gap-3 text-start">
        <span
          className="flex size-9 shrink-0 items-center justify-center bg-muted text-small font-semibold text-foreground"
          aria-hidden="true"
        >
          {event.actor.type === 'system' ? (
            <CogIcon className="size-4" />
          ) : (
            event.actor.initials || actorName.slice(0, 2).toUpperCase()
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-body font-medium">
            <button
              type="button"
              onClick={onOpen}
              className="text-start hover:underline focus-visible:outline-2 focus-visible:outline-ring"
            >
              {actorName} <span className="text-muted-foreground">{action}</span>
            </button>
            {href ? (
              <a href={href} className="text-brand underline-offset-4 hover:underline">
                {recordLabel}
              </a>
            ) : (
              <button
                type="button"
                onClick={onOpen}
                className="text-start hover:underline focus-visible:outline-2 focus-visible:outline-ring"
              >
                {recordLabel}
              </button>
            )}
          </span>
          <span className="mt-1 flex flex-wrap items-center gap-2 text-small text-muted-foreground">
            <Badge tone="neutral">{event.module.label}</Badge>
            <time
              dateTime={event.occurred_at}
              title={formatDateTime(new Date(event.occurred_at), locale)}
            >
              {new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(
                new Date(event.occurred_at),
              )}
            </time>
            {rows.length > 1 ? (
              <span>{t('companyAudit.timeline.requestChanges', { count: rows.length })}</span>
            ) : null}
          </span>
        </span>
      </div>
      {changes.length ? (
        <div className="pt-3 ps-12">
          <ul className="grid gap-2">
            {(expanded ? changes : changes.slice(0, 3)).map((change) => (
              <li key={change.key} className="flex flex-wrap items-baseline gap-x-2 text-small">
                <span className="text-muted-foreground">{change.label}</span>
                {change.masked ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger className="inline-flex items-center gap-1 text-muted-foreground">
                        <LockKeyholeIcon className="size-3.5" />
                        {t('companyAudit.timeline.hidden')}
                      </TooltipTrigger>
                      <TooltipContent>{t('companyAudit.timeline.maskedTooltip')}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <>
                    {change.actionKind !== 'created' ? (
                      <span
                        className={
                          change.old === null
                            ? 'text-muted-foreground'
                            : 'text-muted-foreground line-through'
                        }
                      >
                        {auditChangeValue(change, 'old', locale) ?? '—'}
                      </span>
                    ) : null}
                    {change.actionKind === 'changed' ? <span aria-hidden="true">→</span> : null}
                    {change.actionKind !== 'deleted' ? (
                      <span
                        className={
                          change.new === null
                            ? 'text-muted-foreground'
                            : 'font-medium text-foreground'
                        }
                      >
                        {auditChangeValue(change, 'new', locale) ?? '—'}
                      </span>
                    ) : null}
                  </>
                )}
              </li>
            ))}
          </ul>
          {changes.length > 3 ? (
            <Button
              variant="ghost"
              size="sm"
              className="mt-2 h-auto px-0 text-small"
              onClick={() => setExpanded((value) => !value)}
            >
              {t(
                expanded ? 'companyAudit.timeline.showLess' : 'companyAudit.timeline.moreChanges',
                { count: changes.length - 3 },
              )}
            </Button>
          ) : null}
        </div>
      ) : null}
    </article>
  )
}
